import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import CourseDropdown from "./CourseDropdown";

const MobileNavbar = ({
  navLinks,
  onContactClick,
  // isScrolled,
  // isOverLightSection,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Prevent body scroll when menu or dropdown is open
  useEffect(() => {
    if (isMobileMenuOpen || isDropdownOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen, isDropdownOpen]);

  const handleLinkClick = (e, href, linkName) => {
    console.log("Mobile link clicked:", linkName, href);
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);

    if (linkName === "Contact Us") {
      e.preventDefault();
      onContactClick();
    } else if (href === "#business") {
      e.preventDefault();
      setTimeout(() => {
        document
          .getElementById("business")
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const navbarClasses = `w-full border-b font-fira-sans transition-all duration-300 border-white/20 bg-transparent`;

  return (
    <div className={navbarClasses}>
      <div className="gutter-x">
        <nav>
          <div className="px-4 md:px-6">
            <div className="flex items-center justify-between h-16 md:h-20">
              {/* Logo on the left */}
              <div className="flex items-center">
                <img
                  src="/home/osnewsvg.svg"
                  alt="Outskill"
                  width="120"
                  height="20"
                  className="h-5 w-auto cursor-pointer"
                  onClick={() => (window.location.href = "/")}
                />
              </div>

              {/* Hamburger menu on the right */}
              <button
                className="flex flex-col items-center justify-center w-8 h-8 space-y-1"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              >
                <div
                  className={`w-6 h-0.5 bg-white transition-all duration-300 ${
                    isMobileMenuOpen ? "rotate-45 translate-y-1.5" : ""
                  }`}
                ></div>
                <div
                  className={`w-6 h-0.5 bg-white transition-all duration-300 ${
                    isMobileMenuOpen ? "opacity-0" : ""
                  }`}
                ></div>
                <div
                  className={`w-6 h-0.5 bg-white transition-all duration-300 ${
                    isMobileMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
                  }`}
                ></div>
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && !isDropdownOpen && (
              <>
                {/* Semi-transparent backdrop - no blur */}
                <div 
                  className="fixed inset-0 bg-black/20 z-40" 
                  onClick={() => setIsMobileMenuOpen(false)}
                ></div>
                {/* Menu component with blur */}
                <div className="fixed inset-x-4 top-20 z-50 bg-[#1A1B1C]/95 backdrop-blur-[34px] border border-white/20 rounded-[2px] p-6 shadow-2xl">
                  {/* Menu Links */}
                  <div className="space-y-2 mb-4">
                    {navLinks.map((link, index) => (
                      <a
                        key={index}
                        href={link.href}
                        onClick={(e) => handleLinkClick(e, link.href, link.name)}
                        className="block text-white text-base font-normal hover:bg-white/5 py-3 px-3 rounded-[2px] transition-all duration-200 cursor-pointer"
                        {...(link.href.startsWith("http")
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>

                  {/* Separator */}
                  <div className="border-t border-white/10 my-4"></div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setIsDropdownOpen(true)}
                      className="flex-1 border bg-white/10 text-white transition-all duration-500 border-white/40 font-medium py-3 rounded-[2px] text-sm hover:bg-white/20"
                    >
                      Explore Courses
                    </button>
                    <button
                      className="flex-1 bg-white hover:bg-gray-100 text-black font-medium py-3 rounded-[2px] transition-all duration-200 text-sm"
                      onClick={() =>
                        window.open("https://platform.outskill.com/", "_blank")
                      }
                    >
                      Login
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Mobile Dropdown Content */}
            {isDropdownOpen && isMobileMenuOpen && (
              <>
                {/* Semi-transparent backdrop - no blur */}
                <div 
                  className="fixed inset-0 bg-black/20 z-40" 
                  onClick={() => {
                    setIsDropdownOpen(false);
                    setIsMobileMenuOpen(false);
                  }}
                ></div>
                {/* Dropdown component with blur */}
                <div className="fixed inset-x-4 top-20 z-50 bg-[#1A1B1C]/95 backdrop-blur-[34px] border border-white/20 rounded-[2px] p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
                  {/* Back button */}
                  <button
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center gap-2 text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-all duration-200 mb-4"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Back
                  </button>

                  <CourseDropdown
                    onClose={() => {
                      setIsDropdownOpen(false);
                      setIsMobileMenuOpen(false);
                    }}
                    variant="mobile"
                  />
                </div>
              </>
            )}
          </div>
        </nav>
      </div>
    </div>
  );
};

MobileNavbar.propTypes = {
  navLinks: PropTypes.array.isRequired,
  onContactClick: PropTypes.func.isRequired,
  isScrolled: PropTypes.bool.isRequired,
  isOverLightSection: PropTypes.bool,
};

export default MobileNavbar;
